import { EDecision, EPlanType } from '@if/core/types'
import { formatCurrencyNoDecimals } from '@if/core/utils'
import { Box, Paper, Typography } from '@mui/material'
import FormattedMerchantPaymentPlan from '@src/containers/ViewCreditApplicationPage/components/FormattedMerchantPaymentPlan'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { SwitchedLoanConditionsDto } from '../../../data/api/swap-applicants-roles/SwitchedLoanConditionsDto'
import { TranslatedEnum } from '../../../data/types/TranslatedEnum'

interface LoanConditionsProps {
  label: string
  loanConditionsDto: SwitchedLoanConditionsDto
  planEnum: Record<EPlanType, TranslatedEnum>
  lang: keyof TranslatedEnum
  isBetter: boolean
}

const LoanConditions: React.FC<LoanConditionsProps> = ({
  label,
  loanConditionsDto: loanConditionsdto,
  planEnum,
  lang,
  isBetter
}) => {
  const { t } = useTranslation()
  const isRefused = loanConditionsdto.decision === EDecision.Refused

  const highlightColor = isRefused
    ? '#f44336'
    : isBetter
    ? '#4caf50'
    : undefined

  const badgeMsg = isRefused
    ? t('credit.refused', 'Declined')
    : isBetter
    ? t('credit.betterOption', 'Better option')
    : undefined

  const paperStyles = {
    flex: 1,
    minWidth: 220,
    p: 2,
    borderRadius: 2,
    position: 'relative' as const,
    ...(highlightColor && { border: `0.3px solid ${highlightColor}` }),
    ...(!isRefused &&
      isBetter && {
        '&::after': {
          content: '""',
          position: 'absolute' as const,
          inset: 0,
          borderRadius: 2,
          border: '0.3px solid #4caf50',
          pointerEvents: 'none' as const,
          zIndex: 1
        }
      })
  }

  return (
    <Paper elevation={2} sx={paperStyles}>
      {badgeMsg && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            bgcolor: highlightColor,
            color: 'white',
            px: 1,
            py: 0.5,
            borderTopRightRadius: 4,
            fontSize: '0.75rem',
            fontWeight: 'bold',
            zIndex: 1
          }}
        >
          {badgeMsg}
        </Box>
      )}
      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
        {label}
      </Typography>
      {loanConditionsdto.merchantPaymentPlan ? (
        <Typography
          variant="body2"
          sx={{
            mb: 1,
            whiteSpace: 'nowrap',
            display: 'flex',
            alignItems: 'center'
          }}
        >
          <span style={{ marginRight: 8 }}>Promo:</span>
          <FormattedMerchantPaymentPlan merchantPaymentPlan={loanConditionsdto.merchantPaymentPlan} />
        </Typography>
      ) : (
        <Typography variant="body2">
          {t('credit.interestRate')}: {loanConditionsdto.interestRate}%
        </Typography>
      )}
      <Typography variant="body2">
        {t('credit.plan')}: {planEnum[loanConditionsdto.planName]?.[lang] ?? loanConditionsdto.planName}
      </Typography>
      <Typography variant="body2">
        {t('credit.authorizedLoanAmount')}: {formatCurrencyNoDecimals(loanConditionsdto.maxLoanAmount)}
      </Typography>
      <Typography variant="body2">
        {t('credit.maxPayment')}: {formatCurrencyNoDecimals(loanConditionsdto.maxMonthlyPayment)}
      </Typography>
      <Typography variant="body2">
        {t('editDecisionDialog.maxTermDuration')}: {loanConditionsdto.maxTermDuration} {t('credit.months')}
      </Typography>
    </Paper>
  )
}

export default LoanConditions
